import type { Condition, Effect, Episode, Target, VariableDef } from './schema.ts';

export type Vars = Record<string, number | boolean>;

/** Initial variables; `seed` (saved series-scoped state) overrides initials for series-scoped vars. */
export function initialVars(episode: Pick<Episode, 'variables'>, seed: Vars = {}): Vars {
  const vars: Vars = {};
  for (const [k, def] of Object.entries(episode.variables)) {
    const seeded = seed[k];
    vars[k] = def.scope === 'series' && seeded !== undefined && typeof seeded === typeof def.initial ? seeded : def.initial;
  }
  return vars;
}

/** The subset of vars that should persist across episodes. */
export function seriesVars(episode: Pick<Episode, 'variables'>, vars: Vars): Vars {
  const out: Vars = {};
  for (const [k, def] of Object.entries(episode.variables)) if (def.scope === 'series' && vars[k] !== undefined) out[k] = vars[k]!;
  return out;
}

export function evaluate(cond: Condition | undefined, vars: Vars): boolean {
  if (!cond) return true;
  if ('all' in cond) return cond.all.every((c) => evaluate(c, vars));
  if ('any' in cond) return cond.any.some((c) => evaluate(c, vars));
  if ('not' in cond) return !evaluate(cond.not, vars);
  const current = vars[cond.var];
  const op = cond.op ?? '==';
  const expected = cond.value ?? true;
  const a = typeof current === 'boolean' ? Number(current) : (current ?? 0);
  const b = typeof expected === 'boolean' ? Number(expected) : expected;
  switch (op) {
    case '==':
      return a === b;
    case '!=':
      return a !== b;
    case '<':
      return a < b;
    case '<=':
      return a <= b;
    case '>':
      return a > b;
    case '>=':
      return a >= b;
  }
}

export function applyEffects(vars: Vars, effects: Effect[], defs: Record<string, VariableDef>): Vars {
  const out = { ...vars };
  for (const e of effects) {
    if ('set' in e) {
      out[e.set] = e.value;
    } else {
      const def = defs[e.add];
      const cur = out[e.add];
      let v = (typeof cur === 'number' ? cur : 0) + e.value;
      if (def?.type === 'int') {
        if (def.min !== undefined) v = Math.max(def.min, v);
        if (def.max !== undefined) v = Math.min(def.max, v);
      }
      out[e.add] = v;
    }
  }
  return out;
}

/** Resolve a target (string or conditional list) to a node id, or undefined when nothing matches. */
export function resolveTarget(target: Target, vars: Vars): string | undefined {
  if (typeof target === 'string') return target;
  for (const t of target) if (evaluate(t.when, vars)) return t.to;
  return undefined;
}

/** All node ids a target can possibly lead to (ignoring conditions). */
export function targetNodeIds(target: Target): string[] {
  return typeof target === 'string' ? [target] : target.map((t) => t.to);
}
